import { useTranslate } from '@refinedev/core';
import {
  Card,
  Col,
  Descriptions,
  Flex,
  Grid,
  Row,
  Typography,
  theme,
} from 'antd';
import type { DescriptionsProps } from 'antd';
import type { PublisherDetailOut } from '@/api/generated/schemas';
import { orDash, useVerticalDescProps } from '@/components/common/show/shared';
import { formatDateTime } from '@/utils/format';
import { PublisherSourceCard } from '@/components/user_catalog/publishers/PublisherSourceCard';
import { PublisherStructureCard } from '@/components/user_catalog/publishers/PublisherStructureCard';
import { PublisherStandingCard } from '@/components/user_catalog/publishers/PublisherStandingCard';
import type { PublisherStandingResult } from '@/hooks/tenancy/usePublisherStanding';

interface PublisherGeneralTabProps {
  record: PublisherDetailOut;
  standing: PublisherStandingResult;
  onSynced: () => void;
}

export const PublisherGeneralTab = ({
  record,
  standing,
  onSynced,
}: PublisherGeneralTabProps) => {
  const t = useTranslate();
  const verticalDescProps = useVerticalDescProps();
  const { token } = theme.useToken();
  const screens = Grid.useBreakpoint();

  const items: DescriptionsProps['items'] = [
    {
      key: 'id',
      label: t('base.fields.id'),
      children: orDash(record.id),
    },
    {
      key: 'slug',
      label: t('user_catalog.publishers.fields.slug'),
      children: orDash(
        record.slug && <Typography.Text code>{record.slug}</Typography.Text>
      ),
    },
    {
      key: 'name',
      label: t('user_catalog.publishers.fields.name'),
      children: orDash(record.name),
    },
    {
      key: 'created_at',
      label: t('base.fields.created_at'),
      children: orDash(formatDateTime(record.created_at)),
    },
    {
      key: 'updated_at',
      label: t('base.fields.updated_at'),
      children: orDash(formatDateTime(record.updated_at)),
    },
  ];

  return (
    <Row gutter={[token.margin, token.margin]}>
      <Col flex="1 1 480px">
        <Flex vertical gap={token.margin}>
          <Card title={t('base.sections.basic_info')}>
            <Descriptions
              {...verticalDescProps}
              column={{ xs: 1, sm: 2, md: 3 }}
              items={items}
            />
          </Card>
          <Card title={t('user_catalog.publishers.fields.description')}>
            <Typography.Paragraph style={{ margin: 0, whiteSpace: 'pre-wrap' }}>
              {orDash(record.description)}
            </Typography.Paragraph>
          </Card>
        </Flex>
      </Col>
      <Col flex={screens.lg ? '0 1 440px' : '0 1 300px'}>
        <Flex vertical gap={token.margin}>
          {record.source_id != null && (
            <PublisherSourceCard record={record} onSynced={onSynced} />
          )}
          <PublisherStandingCard result={standing} />
          <PublisherStructureCard record={record} />
        </Flex>
      </Col>
    </Row>
  );
};
